<template>
  <div class="pa-3 ma-3">
    <v-alert-dialog ref="alert" />

    <!-- Tabla de solicitantes -->
    <v-data-table
      :headers="headers"
      :items="solicitantes_filtrados"
      :loading="loading"
      item-key="id_puesto"
      :items-per-page="-1"
      hide-default-footer
    >
      <template v-slot:[`item.nombre`]="{ item }">
        <div class="d-flex align-center" style="gap:8px;">
          <span class="font-weight-medium">{{ item.nombre }}</span>
          <v-tooltip bottom v-if="!item.puede_solicitar_vacantes">
            <template v-slot:activator="{ on, attrs }">
              <v-icon x-small color="grey" v-on="on" v-bind="attrs">mdi-information</v-icon>
            </template>
            <span>Este puesto actualmente no tiene permiso general para solicitar vacantes.</span>
          </v-tooltip>
        </div>
        <div class="caption grey--text">{{ nombre_area_por_id(item.id_area) || '—' }}</div>
      </template>

      <template v-slot:[`item.resumen`]="{ item }">
        <div v-if="permiso_de(item.id_puesto)?.grant_all" class="d-flex align-center justify-center">
          <v-icon small color="green" class="mr-1">mdi-check-all</v-icon>
          <span>Puede solicitar <strong>cualquier</strong> puesto</span>
        </div>
        <div v-else class="d-flex align-center justify-center">
          <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon>
          <span>{{ (permiso_de(item.id_puesto)?.allowed_ids || []).length }} puestos permitidos</span>
        </div>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn small color="primary" text v-on="on" v-bind="attrs" @click="abrir_dialogo(item)">
              <v-icon small class="mr-1">mdi-cog</v-icon> Configurar
            </v-btn>
          </template>
          <span>Definir qué puestos puede solicitar “{{ item.nombre }}”</span>
        </v-tooltip>
      </template>

      <template v-slot:no-data>
        <div class="text-center my-6">
          <div class="subtitle-1 mb-2">Sin datos</div>
        </div>
      </template>
    </v-data-table>

    <!-- Diálogo de configuración -->
    <v-dialog v-model="dialog_config" max-width="980px" persistent>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <div>
            Configurar permisos ·
            <span class="font-weight-medium">{{ edited_item?.nombre || '—' }}</span>
            <div class="caption grey--text">
              Área: {{ nombre_area_por_id(edited_item?.id_area) || '—' }}
            </div>
          </div>
          <v-spacer />
          <v-switch
            v-model="config.grant_all"
            inset
            hide-details
            label="Puede solicitar cualquier puesto"
          />
        </v-card-title>

        <v-card-text v-if="config.grant_all">
          <v-sheet
            color="blue lighten-5"
            rounded
            class="pa-4 d-flex align-center"
            style="border: 1px solid #90caf9;"
          >
            <v-icon color="blue darken-2" size="32" class="mr-3">mdi-check-all</v-icon>
            <div>
              <div class="subtitle-1 font-weight-medium blue--text text--darken-2">
                Permiso general activo
              </div>
              <div class="body-2">
                Este puesto puede solicitar <strong>cualquier puesto</strong>,
                por lo que <strong>no es necesario especificar</strong> permisos individuales.
              </div>
            </div>
          </v-sheet>
        </v-card-text>


        <v-card-text v-else>
          <v-row dense>
            <v-col cols="12" md="4">
              <v-select
                v-model="filtro_area"
                :items="areas_select"
                item-text="nombre"
                item-value="id_area"
                clearable
                label="Filtrar por área"
                outlined dense hide-details
              />
            </v-col>
            <v-col cols="12" md="8">
              <v-text-field
                v-model="filtro_texto"
                label="Buscar puesto"
                append-icon="mdi-magnify"
                outlined dense hide-details
                clearable
              />
            </v-col>
          </v-row>

          <v-row dense class="mb-2">
            <v-col cols="12" class="d-flex" style="gap:8px;">
              <v-btn small outlined color="primary" @click="seleccionar_todos_visibles" :disabled="config.grant_all">Seleccionar todos visibles</v-btn>
              <v-btn small outlined color="primary" @click="limpiar_seleccion" :disabled="config.grant_all">Limpiar selección</v-btn>
              <v-spacer />
              <div class="caption">
                Seleccionados: <strong>{{ seleccionados_count }}</strong> / {{ candidatos_filtrados.length }}
              </div>
            </v-col>
          </v-row>

          <v-simple-table class="tabla-objetivos" dense>
            <thead>
              <tr>
                <th style="width:56px;" class="text-center">Sel.</th>
                <th>Puesto</th>
                <th style="width:30%;">Área</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="p in candidatos_filtrados"
                :key="p.id_puesto"
                :class="row_class(p)"
              >
                <td class="text-center">
                  <v-checkbox
                    :input-value="esta_seleccionado(p.id_puesto)"
                    :disabled="config.grant_all"
                    hide-details dense class="ma-0 pa-0"
                    @change="toggle_objetivo(p.id_puesto, $event)"
                  />
                </td>
                <td>
                  <div>{{ p.nombre }}</div>
                </td>
                <td>
                  <div class="caption">{{ nombre_area_por_id(p.id_area) || '—' }}</div>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text color="green" @click="cerrar_dialogo" :disabled="saving">Cancelar</v-btn>
          <v-btn text color="red" @click="guardar_config" :loading="saving">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from "axios";
import VAlertDialog from "@/components/VAlertDialog.vue";

export default {
  name: "PermisosSolicitudVacantesPorPuesto",
  components: { VAlertDialog },
  data: () => ({
    loading: false,
    saving: false,

    puestos: [],     // todos los puestos (solicitantes y objetivos)
    areas: [],       // catálogo de áreas (mock si no tienes endpoint)

    // Permisos: { [id_puesto_solicitante]: { grant_all: boolean, allowed_ids: number[] } }
    permisos: {},

    mostrar_todos: false,

    // tabla principal
    headers: [
      { text: "Puesto solicitante", value: "nombre", align: "start", width: "45%" },
      { text: "Resumen", value: "resumen", align: "center", width: "35%" },
      { text: "Acciones", value: "actions", align: "center", width: "20%" }
    ],

    // diálogo config
    dialog_config: false,
    edited_item: null,
    config: {
      grant_all: false,
      allowed_ids: []
    },

    // filtros dentro del diálogo
    filtro_area: null,
    filtro_texto: ""
  }),

  computed: {
    solicitantes_filtrados() {
      const base = this.mostrar_todos
        ? this.puestos
        : this.puestos.filter(p => p.puede_solicitar_vacantes);

      // Ordenar por nombre
      return [...base].sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
    },

    areas_select() {
      return [{ id_area: null, nombre: "Todas las áreas" }, ...this.areas];
    },

    candidatos_filtrados() {
      let arr = this.puestos;

      if (this.edited_item) {
        // opcional: evita que se seleccione a sí mismo
        arr = arr.filter(p => p.id_puesto !== this.edited_item.id_puesto);
      }
      if (this.filtro_area) {
        arr = arr.filter(p => p.id_area === this.filtro_area);
      }
      if (this.filtro_texto && this.filtro_texto.trim().length) {
        const q = this.filtro_texto.trim().toLowerCase();
        arr = arr.filter(p => p.nombre.toLowerCase().includes(q));
      }
      // Orden por área y nombre
      return arr.sort((a, b) => {
        const an = this.nombre_area_por_id(a.id_area) || "";
        const bn = this.nombre_area_por_id(b.id_area) || "";
        if (an === bn) return a.nombre.localeCompare(b.nombre, "es");
        return an.localeCompare(bn, "es");
      });
    },

    seleccionados_count() {
      return this.config.grant_all ? this.puestos.length - 1 : this.config.allowed_ids.length;
    }
  },

  mounted() {
    this.cargar_puestos();
    this.cargar_permisos_local();
    setTimeout(() => {
      this.cargar_mock();
    }, 1000);
  },

  methods: {
    // ====================
    // Carga de datos
    // ====================
    async cargar_puestos() {
      this.loading = true;
      try {
        const resp = await axios.get("/RecursosHumanos/ListarPuestos");
        const data = Array.isArray(resp?.data) ? resp.data : [];

        // Si no tienes catálogo de áreas real, usa mock para nombres
        if (!this.areas.length) this.areas = this.mock_areas();

        // Normaliza puestos y completa faltantes
        this.puestos = (data.length ? data : this.mock_puestos()).map(x => ({
          id_puesto: x.id_puesto,
          nombre: x.nombre || x.nombre_puesto || "(sin nombre)",
          id_area: x.id_area ?? this.areas[0]?.id_area ?? 1,
          puede_solicitar_vacantes: typeof x.puede_solicitar_vacantes === "boolean" ? x.puede_solicitar_vacantes : false
        }));
      } catch (e) {
        console.error(e);
        this.puestos = this.mock_puestos();
        if (!this.areas.length) this.areas = this.mock_areas();
        this.$refs.alert.setMessage("Aviso", "No se pudieron cargar los puestos. Se mostrarán datos de demo.");
        this.$refs.alert.open();
      } finally {
        this.loading = false;
      }
    },

    // ====================
    // Diálogo
    // ====================
    abrir_dialogo(item) {
      this.edited_item = JSON.parse(JSON.stringify(item));

      const perm = this.permiso_de(item.id_puesto);
      this.config = {
        grant_all: !!perm?.grant_all,
        allowed_ids: Array.isArray(perm?.allowed_ids) ? [...perm.allowed_ids] : []
      };

      // limpiar filtros
      this.filtro_area = null;
      this.filtro_texto = "";

      this.dialog_config = true;
    },
    cerrar_dialogo() {
      this.dialog_config = false;
      this.edited_item = null;
      this.config = { grant_all: false, allowed_ids: [] };
    },

    // ====================
    // Acciones en diálogo
    // ====================
    esta_seleccionado(id_puesto_obj) {
      return this.config.allowed_ids.includes(id_puesto_obj);
    },
    toggle_objetivo(id_puesto_obj, value) {
      const set = new Set(this.config.allowed_ids);
      if (value) set.add(id_puesto_obj);
      else set.delete(id_puesto_obj);
      this.config.allowed_ids = Array.from(set);
    },
    seleccionar_todos_visibles() {
      if (this.config.grant_all) return;
      const visibles = this.candidatos_filtrados.map(x => x.id_puesto);
      const set = new Set(this.config.allowed_ids);
      visibles.forEach(id => set.add(id));
      this.config.allowed_ids = Array.from(set);
    },
    limpiar_seleccion() {
      if (this.config.grant_all) return;
      this.config.allowed_ids = [];
    },
    row_class(p) {
      return this.esta_seleccionado(p.id_puesto) ? "row-selected" : "";
    },

    async guardar_config() {
      if (!this.edited_item) return;

      try {
        this.saving = true;

        // Simulación de API (latencia)
        await new Promise(res => setTimeout(res, 600));

        // Validación simple
        if (!this.config.grant_all && this.config.allowed_ids.length === 0) {
          // Permitimos guardar sin selección (significa "no puede solicitar ninguno")
        }

        // Guardar en memoria y persistir localmente
        this.$set(this.permisos, this.edited_item.id_puesto, {
          grant_all: !!this.config.grant_all,
          allowed_ids: this.config.grant_all
            ? [] // si aplica a todos, la lista específica queda vacía
            : [...this.config.allowed_ids]
        });
        this.guardar_permisos_local();
        
        this.cerrar_dialogo();
      } catch (e) {
        console.error(e);
        this.$refs.alert.setMessage("Error", "No se pudieron guardar los permisos (simulado).");
        this.$refs.alert.open();
      } finally {
        this.saving = false;
      }
    },

    // ====================
    // Helpers permisos
    // ====================
    permiso_de(id_puesto_solicitante) {
      return this.permisos[id_puesto_solicitante] || { grant_all: false, allowed_ids: [] };
    },
    puede_solicitar(id_puesto_solicitante, id_puesto_objetivo) {
      const p = this.permiso_de(id_puesto_solicitante);
      if (p.grant_all) return true;
      return p.allowed_ids.includes(id_puesto_objetivo);
    },

    // ====================
    // Persistencia local
    // ====================
    guardar_permisos_local() {
      try {
        localStorage.setItem("vac_req_perms", JSON.stringify(this.permisos));
      } catch(error) {
        console.log(error)
      }
    },
    cargar_permisos_local() {
      try {
        const raw = localStorage.getItem("vac_req_perms");
        if (raw) this.permisos = JSON.parse(raw);
      } catch(error) {
        console.log(error)
      }
    },

    // ====================
    // Catalog helpers
    // ====================
    nombre_area_por_id(id_area) {
      return this.areas.find(a => a.id_area === id_area)?.nombre || null;
    },

    // ====================
    // Mock data
    // ====================
    mock_areas() {
      return [
        { id_area: 1, nombre: "Tecnologías de la Información (IT)" },
        { id_area: 2, nombre: "Recursos Humanos" },
        { id_area: 3, nombre: "Administrativo" },
        { id_area: 4, nombre: "Operativo" }
      ];
    },
    




    // --- Helpers de asignación de áreas por nombre ---
    get_area_id_por_nombre(nombre) {
      const n = (nombre || "").toLowerCase();

      // IT (1)
      if (n.includes("tecnologías de la información") || n.includes("ti") || n.includes("radiólogo") && n.includes("técnico") === false) {
        // Nota: "Médico radiólogo" y "Técnico radiólogo" los mandaremos a Operativo (4) más abajo
        // Aquí solo capturamos "Responsable de tecnologías de la información" o "Auxiliar de tecnologías de la información" etc.
      }
      if (n.includes("tecnologías de la información") || n.includes(" ti")) return 1;

      // Recursos Humanos (2)
      if (n.includes("recursos humanos")) return 2;

      // Operativo (4) — clínicos, campo, mantenimiento, operadores, promotores, centro operativo/farmacia
      if (
        n.includes("médico") ||
        n.includes("enfermera") ||
        n.includes("dentista") ||
        n.includes("nutriólogo") ||
        n.includes("técnico radiólogo") ||
        n.includes("operación") ||
        n.includes("operativo") ||
        n.includes("mantenimiento") ||
        n.includes("promotor") ||
        n.includes("centro operativo") ||
        n.includes("farmacia") ||
        n.includes("psicóloga") ||
        n.includes("sanitario")
      ) return 4;

      // Administrativo (3) — lo demás: dirección, presidencia, contabilidad, tesorería, compras, comunicación, seguimiento adm/legal, calidad, desarrollo institucional
      return 3;
    },

    mock_puestos() {
      // Catálogo de nombres exactos
      const nombres = [
        "Presidencia",
        "Asesor médico",
        "Auxiliar administrativo de proyecto",
        "Auxiliar de análisis y seguimiento",
        "Auxiliar de centro operativo y farmacia",
        "Auxiliar de comunicación",
        "Auxiliar de contabilidad",
        "Auxiliar de logística",
        "Auxiliar de mantenimiento",
        "Auxiliar de operación",
        "Auxiliar de recursos humanos",
        "Auxiliar de seguimiento administrativo y legal",
        "Auxiliar de tecnologías de la información",
        "Coordinador general operativo",
        "Dentista",
        "Dirección general",
        "Enfermera (o)",
        "Jefe de departamento operativo",
        "Médico general",
        "Médico radiólogo",
        "Nutriólogo",
        "Operador polivalente",
        "Promotor de salud",
        "Psicóloga",
        "Responsable de análisis y seguimiento",
        "Responsable de Calidad",
        "Responsable de compras",
        "Responsable de comunicación",
        "Responsable de contabilidad",
        "Responsable de desarrollo institucional",
        "Responsable de mantenimiento",
        "Responsable de normas médicas",
        "Responsable de recursos humanos",
        "Responsable de relaciones institucionales",
        "Responsable de seguimiento administrativo y legal",
        "Responsable de seguimiento operativo",
        "Responsable de tecnologías de la información",
        "Responsable de tesorería",
        "Responsable sanitario de centro operativo y farmacia",
        "Técnico radiólogo"
      ];

      // Quienes pueden solicitar (bandera general)
      const solicitantes_true = new Set([
        "Presidencia",
        "Dirección general",
        "Responsable de recursos humanos",
        "Jefe de departamento operativo",
        "Coordinador general operativo",
        "Responsable de tecnologías de la información"
      ]);

      // Construimos el arreglo normalizado
      const puestos = nombres.map((nombre, idx) => {
        const id_puesto = idx + 1;
        const id_area = this.get_area_id_por_nombre(nombre);
        const puede = solicitantes_true.has(nombre);
        return { id_puesto, nombre, id_area, puede_solicitar_vacantes: puede };
      });

      return puestos;
    },

    cargar_mock() {
      this.puestos = this.mock_puestos();
      if (!this.areas.length) this.areas = this.mock_areas();

      // Helper para obtener id por nombre (evita fallos si se cambia el orden)
      const idBy = (nombreBuscado) => {
        const p = this.puestos.find(p => p.nombre === nombreBuscado);
        return p ? p.id_puesto : null;
      };

      // Agrupaciones por conveniencia
      const operativos = [
        "Coordinador general operativo",
        "Jefe de departamento operativo",
        "Operador polivalente",
        "Auxiliar de operación",
        "Responsable de seguimiento operativo",
        "Auxiliar de centro operativo y farmacia",
        "Responsable sanitario de centro operativo y farmacia",
        "Auxiliar de mantenimiento",
        "Responsable de mantenimiento",
        "Promotor de salud"
      ];

      const clinicos = [
        "Médico general",
        "Médico radiólogo",
        "Técnico radiólogo",
        "Dentista",
        "Enfermera (o)",
        "Nutriólogo",
        "Psicóloga",
        "Asesor médico",
        "Responsable de normas médicas",
        "Responsable de Calidad"
      ];

      const ti = [
        "Responsable de tecnologías de la información",
        "Auxiliar de tecnologías de la información"
      ];

      const administrativos = [
        "Presidencia",
        "Dirección general",
        "Responsable de contabilidad",
        "Auxiliar de contabilidad",
        "Responsable de tesorería",
        "Responsable de compras",
        "Auxiliar administrativo de proyecto",
        "Auxiliar de logística",
        "Responsable de comunicación",
        "Auxiliar de comunicación",
        "Responsable de desarrollo institucional",
        "Responsable de relaciones institucionales",
        "Responsable de análisis y seguimiento",
        "Auxiliar de análisis y seguimiento",
        "Responsable de seguimiento administrativo y legal",
        "Auxiliar de seguimiento administrativo y legal",
        "Auxiliar de recursos humanos",
        "Responsable de recursos humanos"
      ];

      // Convierte nombres a IDs (ignora los que no encuentre)
      const ids = (arr) => arr.map(idBy).filter(Boolean);

      // Permisos demo:
      this.permisos = {
        // Presidencia -> puede solicitar a todos
        [idBy("Presidencia")]: { grant_all: true, allowed_ids: [] },

        // Dirección general -> puede solicitar a todos
        [idBy("Dirección general")]: { grant_all: true, allowed_ids: [] },

        // Responsable de recursos humanos -> administrativos + operativos base + clínicos generales
        [idBy("Responsable de recursos humanos")]: {
          grant_all: false,
          allowed_ids: [
            ...ids(administrativos),
            ...ids(operativos),
            ...ids(clinicos)
          ]
        },

        // Jefe de departamento operativo -> operativos + clínicos
        [idBy("Jefe de departamento operativo")]: {
          grant_all: false,
          allowed_ids: [
            ...ids(operativos),
            ...ids(clinicos)
          ]
        },

        // Coordinador general operativo -> operativos + clínicos
        [idBy("Coordinador general operativo")]: {
          grant_all: false,
          allowed_ids: [
            ...ids(operativos),
            ...ids(clinicos)
          ]
        },

        // Responsable de TI -> solo TI (y soporte administrativo si quieres agregar)
        [idBy("Responsable de tecnologías de la información")]: {
          grant_all: false,
          allowed_ids: [
            ...ids(ti)
          ]
        }
      };

      // Limpia posibles claves nulas (por si algún idBy devolvió null)
      Object.keys(this.permisos).forEach(k => {
        if (k === "null" || k === "undefined") delete this.permisos[k];
      });

      this.guardar_permisos_local();
    }

  }
};
</script>

<style scoped>
.tabla-objetivos tbody tr.row-selected {
  background-color: rgba(1, 111, 200, 0.06); /* azul tenue */
}
</style>
